import { FastForward, Pause, Play, Rewind, Scissors, Snowflake, Volume1, Volume2, VolumeX, X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { fmt } from '../format'
import type { PlayerSlot, SlotHandlers, SlotKey } from '../hooks/useClipPlayback'
import { isFullFrame, type CropRect, type ProjectVideo } from '../model'
import type { Armed } from './Timeline'
import styles from '../styles/editor.module.scss'

export interface VideoStageProps {
  video: ProjectVideo
  /** The two stacked players — see ``useClipPlayback`` for why there are
   * two. Only the active one is visible. */
  slots: PlayerSlot[]
  handlersFor: (slot: SlotKey) => SlotHandlers
  videos: ProjectVideo[]
  clipCountByVideo: Record<string, number>
  onSelectVideo: (videoId: string) => void
  onRemoveVideo: (video: ProjectVideo) => void

  playing: boolean
  playhead: number
  duration: number
  armed: Armed | null
  /** Number the clip being marked will get in the list. */
  nextClipNumber: number

  volume: number
  muted: boolean
  onVolumeChange: (value: number) => void
  onToggleMute: () => void
  /** Caption layer, rendered inside the video box so it shares the frame's
   * coordinate space. Passed in rather than built here — positioning them
   * is project state, and this component owns none. */
  overlay?: React.ReactNode
  /**
   * The project's framing, or ``null`` for the whole frame. When set, the
   * box takes the **cropped** aspect and the picture is scaled and offset
   * inside it — so the caption layer, which spans the box, keeps measuring
   * fractions of the frame that actually gets exported.
   */
  crop?: CropRect | null
  /** While the crop is being edited the full source frame is shown instead,
   * because you can't position a rectangle over a picture that's already
   * been cut down to it. */
  cropEditing?: boolean
  /** Crop handles, drawn over the full frame. Only rendered while editing. */
  cropOverlay?: React.ReactNode
  /** CSS ``filter`` from the colour grades covering this instant, or
   * ``'none'``. Applied to the picture only — grading the caption layer too
   * would tint text the export leaves alone. */
  videoFilter?: string
  /** True while the playhead is inside a held frame. The transport says so,
   * because a paused-looking player that still counts up otherwise reads as
   * a bug. */
  frozen?: boolean

  onTogglePlay: () => void
  /** Click on the picture itself. Separate from the transport button so
   * clicking the video can also drop the caption selection without the
   * play button doing the same. */
  onVideoClick?: (() => void) | undefined
  onSeekBy: (delta: number) => void
  onToggleArm: () => void
}

/** Player + transport bar + (when the project has more than one) the
 * source-video switcher. */
export function VideoStage({
  video,
  slots,
  handlersFor,
  videos,
  clipCountByVideo,
  onSelectVideo,
  onRemoveVideo,
  playing,
  playhead,
  duration,
  armed,
  nextClipNumber,
  volume,
  muted,
  onVolumeChange,
  onToggleMute,
  overlay,
  crop,
  cropEditing = false,
  cropOverlay,
  videoFilter,
  frozen = false,
  onTogglePlay,
  onVideoClick,
  onSeekBy,
  onToggleArm,
}: VideoStageProps) {
  const VolumeIcon = muted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2
  const percent = Math.round((muted ? 0 : volume) * 100)

  // The crop applies **while it is being edited too**. Opening the tool used
  // to un-crop the picture, which meant picking a ratio changed nothing on
  // screen until you closed the tool again — the framing was invisible
  // exactly while you were choosing it.
  const applied = isFullFrame(crop ?? null) ? null : (crop ?? null)
  // Null until something reports the source's dimensions. It can stay null
  // for a while — a video added from "já no servidor" has none until a
  // probe element loads its ``moov`` — so nothing below may *depend* on it
  // being a number. Collapsing the frame to nothing when it isn't is
  // exactly how the stage went black the moment the framing tool opened.
  const sourceAspect = video.width && video.height ? video.width / video.height : null
  const cropAspect =
    sourceAspect === null
      ? null
      : applied
        ? (sourceAspect * applied.width) / applied.height
        : sourceAspect

  // While framing, the *box* keeps the source's shape and the output frame
  // is letterboxed inside it. Letting the box follow the crop would reflow
  // it on every mousemove of a corner drag, scaling the picture out from
  // under the cursor. Outside the tool the box is the output frame, so
  // ordinary editing has no letterbox.
  const boxAspect = cropEditing ? sourceAspect : cropAspect

  // The output frame, as a share of the box — always both axes, so the
  // element can never end up with no size at all.
  const viewportStyle: React.CSSProperties = { width: '100%', height: '100%' }
  if (cropEditing && cropAspect !== null && boxAspect !== null) {
    if (cropAspect <= boxAspect) {
      viewportStyle.width = `${String((cropAspect / boxAspect) * 100)}%`
    } else {
      viewportStyle.height = `${String((boxAspect / cropAspect) * 100)}%`
    }
  }

  // Scale the picture up by 1/size and pull it back by the offset, so the
  // visible window is exactly the fraction the export will cut.
  const cropStyle: React.CSSProperties | undefined = applied
    ? {
        width: `${String(100 / applied.width)}%`,
        height: `${String(100 / applied.height)}%`,
        left: `${String((-applied.x / applied.width) * 100)}%`,
        top: `${String((-applied.y / applied.height) * 100)}%`,
        // `.video` sets `inset: 0`; leaving right/bottom pinned would
        // over-constrain the box and the browser would drop our width.
        right: 'auto',
        bottom: 'auto',
      }
    : undefined

  return (
    <>
      {videos.length > 1 ? (
        <div className={styles.videoTabs} role="tablist" aria-label="Vídeos do projeto">
          {videos.map((v, i) => (
            <div
              key={v.id}
              className={cn(styles.videoTab, v.id === video.id && styles.videoTabActive)}
            >
              <button
                type="button"
                role="tab"
                aria-selected={v.id === video.id}
                className={styles.videoTabMain}
                onClick={() => {
                  onSelectVideo(v.id)
                }}
                title={v.filename}
              >
                <span className={styles.videoTabIndex}>{i + 1}</span>
                <span className={styles.videoTabName}>{v.filename}</span>
                {clipCountByVideo[v.id] ? (
                  <span className={styles.videoTabCount}>{clipCountByVideo[v.id]}</span>
                ) : null}
              </button>
              <button
                type="button"
                className={styles.videoTabRemove}
                onClick={() => {
                  onRemoveVideo(v)
                }}
                aria-label={`Remover ${v.filename} do projeto`}
                title="Remover do projeto (o arquivo continua no servidor)"
              >
                <X aria-hidden />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <div className={styles.videoFrame}>
        <div
          className={styles.videoBox}
          style={boxAspect ? { ['--video-aspect' as string]: String(boxAspect) } : undefined}
        >
          {armed ? (
            <div className={styles.clipBadge}>
              <span className={styles.clipDot} />
              <span>
                Clipe {nextClipNumber} ·{' '}
                {armed.phase === 'start' ? 'marque o INÍCIO' : 'marque o FIM'}
              </span>
            </div>
          ) : null}
          {/* The output frame. Everything measured in fractions of the
              exported video — the caption layer above all — lives inside
              it, so those fractions mean the same thing here and in the
              burn-in whether or not the framing tool is open. */}
          <div
            className={cn(styles.cropViewport, cropEditing && styles.cropViewportFramed)}
            style={viewportStyle}
          >
            {/* The grade goes on this wrapper rather than on each element:
                one filter for both players, and the caption layer stays
                outside it — the export doesn't grade the text either. */}
            <div
              className={styles.gradeLayer}
              style={videoFilter && videoFilter !== 'none' ? { filter: videoFilter } : undefined}
            >
              {slots.map((slot) => {
                const handlers = handlersFor(slot.key)
                return (
                  // The source is an arbitrary user file: there is no caption
                  // track to offer, and this player is an editing surface, not
                  // published content.
                  // eslint-disable-next-line jsx-a11y/media-has-caption
                  <video
                    key={slot.key}
                    ref={slot.ref}
                    className={cn(styles.video, !slot.active && styles.videoIdle)}
                    style={cropStyle}
                    {...(slot.src ? { src: slot.src } : {})}
                    // ``auto`` on both: the hidden one is only useful if it
                    // actually buffers the next clip ahead of the swap.
                    preload="auto"
                    playsInline
                    onLoadedMetadata={handlers.onLoadedMetadata}
                    onTimeUpdate={handlers.onTimeUpdate}
                    onPlay={handlers.onPlay}
                    onPause={handlers.onPause}
                    onEnded={handlers.onPause}
                    onClick={slot.active ? (onVideoClick ?? onTogglePlay) : undefined}
                  />
                )
              })}
            </div>
            {/* Captions stay on screen while framing, and stay put: they
                measure this box, which is the output frame in both states. */}
            {overlay}
            {cropEditing ? cropOverlay : null}
          </div>
          {frozen ? (
            <div className={styles.frozenBadge}>
              <Snowflake aria-hidden />
              <span>Frame congelado</span>
            </div>
          ) : null}
          {!playing ? (
            <div className={styles.playOverlay} aria-hidden>
              <button
                type="button"
                className={styles.bigPlay}
                onClick={onTogglePlay}
                aria-label="Reproduzir"
              >
                <Play />
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className={styles.transport}>
        <button
          type="button"
          className={styles.tBtn}
          onClick={() => {
            onSeekBy(-5)
          }}
          aria-label="Voltar 5 segundos"
        >
          <Rewind />
        </button>
        <button
          type="button"
          className={cn(styles.tBtn, styles.tBtnPlay)}
          onClick={onTogglePlay}
          aria-label={playing ? 'Pausar' : 'Reproduzir'}
        >
          {playing ? <Pause /> : <Play />}
        </button>
        <button
          type="button"
          className={styles.tBtn}
          onClick={() => {
            onSeekBy(5)
          }}
          aria-label="Avançar 5 segundos"
        >
          <FastForward />
        </button>
        <div className={styles.tTime}>
          <span>{fmt(playhead)}</span>
          <span className={styles.tSep}>/</span>
          <span>{fmt(duration)}</span>
        </div>

        <div className={styles.volume}>
          <button
            type="button"
            className={styles.tBtn}
            onClick={onToggleMute}
            aria-label={muted ? 'Ativar som' : 'Silenciar'}
            title={muted ? 'Ativar som' : 'Silenciar'}
          >
            <VolumeIcon />
          </button>
          <input
            type="range"
            className={styles.volumeSlider}
            min={0}
            max={1}
            step={0.01}
            // Muted shows an empty slider: the number on screen should be
            // what you're hearing, not what you'd hear if you unmuted.
            value={muted ? 0 : volume}
            onChange={(e) => {
              onVolumeChange(Number(e.target.value))
            }}
            aria-label="Volume"
            aria-valuetext={`${percent}%`}
            title={`Volume ${percent}%`}
          />
        </div>

        <div className={styles.tSpacer} />
        <button
          type="button"
          className={cn(styles.newClipBtn, armed && styles.newClipBtnArming)}
          onClick={onToggleArm}
        >
          {armed ? <X /> : <Scissors />}
          <span>{armed ? 'Cancelar' : 'Novo clipe'}</span>
        </button>
      </div>
    </>
  )
}
